import React, { memo, useCallback, useReducer } from 'react';
import { View, Switch } from 'react-native';
import { FormFixed } from '@redlink/components/organisms';
import {
  PreviewActiveTabItem,
  PreviewTabItemSelectable,
} from '@redlink/components/muscles';
import { Text, Divider, Space } from '@redlink/components/atoms';
import {
  FormSection,
  ProfileSettingItems,
} from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import { createUseStyle } from '@redlink/assets/styles';
import { useMutation, editSettingQuery } from '@redlink/services';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const ProfileSettingSection = memo(({ data, pageName }) => {
  const { dictionary } = useLocale();
  const { staticData } = useValues();
  const { styles, colors } = useStyle();

  const [{ defaultTab, visibleTabs, showQrCode }, dispatch] = useReducer(
    reducer,
    initialState(data)
  );

  const renderSelectTabItemVisibility = useCallback(
    ({ item }, index) => {
      const isSelected = visibleTabs.some((p) => p === item);
      return (
        <PreviewTabItemSelectable
          {...{ item, isSelected }}
          onChange={(value) => {
            const newVisibleTabs = value
              ? [...visibleTabs, item]
              : [...visibleTabs.filter((p) => p !== item)];

            newVisibleTabs.length &&
              dispatch({
                type: types.SET_VISIBLE_TABS,
                payload: newVisibleTabs,
              });
          }}
          {...{ defaultTab }}
        />
      );
    },
    [visibleTabs, defaultTab]
  );

  const renderActiveTabsItem = useCallback(
    ({ item }, index) => {
      const isSelected = defaultTab === item;
      return (
        <PreviewActiveTabItem
          {...{ item, isSelected }}
          onChange={() => {
            const isSelectable = visibleTabs.includes(item);
            isSelectable &&
              dispatch({
                type: types.SET_DEFAULT_TAB,
                payload: item,
              });
          }}
        />
      );
    },
    [defaultTab, visibleTabs]
  );

  const [editSetting, { loading: editSettingIsBusy }] = useMutation(
    editSettingQuery,
    {
      update: freshCache({ pageName }),
    }
  );

  const haveItemsChanged =
    data.visibleTabs.length !== visibleTabs.length ||
    data.defaultTab !== defaultTab ||
    data.showQrCode !== showQrCode;

  return (
    <FormFixed
      isBusy={editSettingIsBusy}
      actionsStyle={styles.actionsContainer}
      actions={[
        {
          variant: 'primary',
          title: dictionary.save,
          disabled: !haveItemsChanged,
          onPress: () => editSetting({ defaultTab, visibleTabs, showQrCode }),
        },
        {
          variant: 'secondary',
          title: dictionary.cancel,
          disabled: !haveItemsChanged,
          onPress: () =>
            dispatch({
              type: types.SET_MULTI,
              payload: {
                visibleTabs: data.visibleTabs,
                defaultTab: data.defaultTab,
                showQrCode: data.showQrCode,
              },
            }),
        },
      ]}
    >
      <FormSection title={dictionary.setting}>
        <ProfileSettingItems
          title={dictionary.visibleTabs}
          description={dictionary.visibleTabsDescription}
          renderItem={renderSelectTabItemVisibility}
          data={staticData.previewTabs}
          style={styles.visibleTabsContainer}
        />
        <Divider margin={10} />
        <ProfileSettingItems
          title={dictionary.selectDefaultTab}
          description={dictionary.selectDefaultTabDescription}
          renderItem={renderActiveTabsItem}
          data={staticData.previewTabs}
          style={styles.visibleTabsContainer}
        />
      </FormSection>
      <Divider margin={10} />
      <View style={[styles.qrCodeContainer]}>
        <Text style={styles.titleText}>{dictionary.showQRcode}</Text>
        <Switch
          trackColor={{ true: colors.green1, false: colors.gray22 }}
          activeThumbColor="white"
          thumbColor="white"
          value={showQrCode}
          onValueChange={() => dispatch({ type: types.SET_SHOW_QR_CODE })}
          style={styles.switch}
        />
      </View>
      <Space />
    </FormFixed>
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  headerText: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '500',
    color: colors.black,
  },
  visibleTabsContainer: {
    marginTop: 0,
  },
  qrCodeContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  qrCodeContainerDisabled: {
    opacity: 0.3,
  },
  switch: {
    width: 43,
    height: 21,
    paddingVertical: 14,
    paddingHorizontal: 0,
    justifyContent: 'center',
    marginEnd: 16,
  },
  actionsContainer: {
    flexDirection: 'row-reverse',
  },
  titleText: {
    color: colors.gray23,
    fontSize: 16,
  },
}));
